import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { resources } from '../../../core/services/resources';
import { extractJobErrorMessage } from '../../../core/services/api';
import { IconTile } from '../../../shared/components/IconTile';
import { Pill } from '../../../shared/components/Pill';
import { hashSemanticColor } from '../../../shared/components/semanticColor';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import type { EntityMarketFeed, EntityTypeEnrichment, EntityTypeSkill } from '../../../core/types/data-models';
import { CardLayoutToggle, type CardLayout } from './CardLayoutToggle';
import { InsightTopicTab } from './InsightTopicTab';
import { MainTab } from './MainTab';
import { MarketFeedTab } from './MarketFeedTab';
import { useEntityTypeEntities } from './useEntityTypeEntities';

const MAIN_TAB = 'main';
const MARKET_FEED_TAB = 'market-feed';

/**
 * Tab key for an insight topic — by skill and entity rather than topic id,
 * since regenerating a topic replaces its row with a new id and an open tab
 * should follow it onto the new content.
 */
const analysisTabKey = (t: EntityTypeEnrichment) =>
  t.skillId == null ? `topic:${t.id}` : `analysis:${t.skillId}:${t.entityId ?? 'type'}`;

/**
 * Right-side view opened from a MANAGED KNOWLEDGE sidebar row — every
 * project entity of one entity type, shown as cards. Same cross-document
 * "mentions" concept as EntityDetailsDialog, but for the whole type at once
 * rather than a single clicked entity.
 *
 * Tabs: Main (skill analyses + entity cards) is always there; Market Feed and
 * insight topics open as closable tabs — from the header's "Market Feed"
 * button, a skill card's "View", or the entity panel's "Open analysis". The
 * two AI buttons in the header each enqueue a project job that is polled
 * here until it finishes.
 */
export function ManagedEntityTypeDetail() {
  const { id, entityType: entityTypeParam } = useParams<{ id: string; entityType: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canGenerate, canEdit } = usePermissions();
  const projectId = Number(id);
  const entityType = decodeURIComponent(entityTypeParam ?? '');

  // `?tab=` deep link — 'main', 'market-feed', or an insight topic id — so the
  // project dashboard's Managed Knowledge block can open straight onto a topic.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');

  const [layout, setLayout] = useState<CardLayout>('column');
  const [activeTab, setActiveTab] = useState<string>(MAIN_TAB);
  // Closable tabs in the order they were opened; Main is not in the list.
  const [openTabs, setOpenTabs] = useState<string[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [genJobId, setGenJobId] = useState<number | null>(null);
  const [genSkillId, setGenSkillId] = useState<number | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [selectedSkillId, setSelectedSkillId] = useState<number | ''>('');
  const [marketFeedJobId, setMarketFeedJobId] = useState<number | null>(null);
  const [marketFeedError, setMarketFeedError] = useState<string | null>(null);

  const enrichmentsQuery = useQuery({
    queryKey: ['managed-knowledge-enrichments', projectId, entityType],
    queryFn: () =>
      resources.entityTypeEnrichments.list({
        filter: { projectId: [projectId], entityType: [entityType] },
        sort: 'order',
        // An ENTITY-scope skill produces one topic per entity, so this can
        // multiply well past the old 100 with a handful of entities.
        pageSize: 1000,
      }),
    enabled: Number.isFinite(projectId) && !!entityType,
  });
  const topics: EntityTypeEnrichment[] = enrichmentsQuery.data?.data ?? [];
  // The header's quick "Generate Insights" dropdown below only ever covers
  // ENTITY_TYPE-scope topics — an ENTITY-scope topic is run from its skill
  // card on the Main tab, for a picked entity.
  const typeTopics = useMemo(() => topics.filter((t) => t.entityId == null), [topics]);
  const topicsByTabKey = useMemo(() => new Map(topics.map((t) => [analysisTabKey(t), t])), [topics]);

  const openTab = (key: string) => {
    setOpenTabs((tabs) => (tabs.includes(key) ? tabs : [...tabs, key]));
    setActiveTab(key);
  };
  const openTopicTab = (topicId: number) => {
    const topic = topics.find((t) => t.id === topicId);
    if (topic) openTab(analysisTabKey(topic));
  };
  // Closing the active tab moves to the tab on its left (Main for the first).
  const closeTab = (key: string) => {
    const index = openTabs.indexOf(key);
    setOpenTabs(openTabs.filter((k) => k !== key));
    if (activeTab === key) setActiveTab(openTabs[index - 1] ?? MAIN_TAB);
  };

  // This view is reused across param changes (not remounted), so start a new
  // entity type from Main with no open tabs, then follow the deep link — once
  // topics have loaded, since a topic id resolves to its tab key through them.
  useEffect(() => {
    setOpenTabs([]);
    setActiveTab(MAIN_TAB);
    setSelectedEntityId(null);
  }, [entityType]);
  useEffect(() => {
    if (enrichmentsQuery.isPending || !requestedTab || requestedTab === MAIN_TAB) return;
    if (requestedTab === MARKET_FEED_TAB) openTab(MARKET_FEED_TAB);
    else openTopicTab(Number(requestedTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, entityType, enrichmentsQuery.isPending]);

  // Drop open analysis tabs whose topic no longer exists (e.g. its skill was
  // removed), falling back to Main if one of them was showing. Functional
  // updates, so this composes with the reset/deep-link effects above when
  // they all run in the same commit.
  useEffect(() => {
    if (enrichmentsQuery.isPending) return;
    const isStale = (k: string) => k !== MAIN_TAB && k !== MARKET_FEED_TAB && !topicsByTabKey.has(k);
    setOpenTabs((tabs) => (tabs.some(isStale) ? tabs.filter((k) => !isStale(k)) : tabs));
    setActiveTab((tab) => (isStale(tab) ? MAIN_TAB : tab));
  }, [enrichmentsQuery.isPending, topicsByTabKey]);

  // Skills available to generate an insight topic from — every scope, for
  // MainTab's Analyses grid.
  const skillsQuery = useQuery({
    queryKey: ['managed-knowledge-skills', entityType],
    queryFn: () =>
      resources.entityTypeSkills.list({
        filter: { entityType, status: 'ACTIVE' },
        sort: 'order',
        pageSize: 200,
      }),
    enabled: !!entityType,
  });
  const skills: EntityTypeSkill[] = skillsQuery.data?.data ?? [];
  // The header's dropdown next to "Generate Insights" below stays
  // ENTITY_TYPE-scope only — an ENTITY-scope skill needs an entity picked
  // first, which only MainTab's Analyses grid offers.
  const headerSkills = useMemo(() => skills.filter((s) => s.scope !== 'ENTITY'), [skills]);

  // Reset the selection when navigating to a different entity type (this
  // view is reused across param changes, not remounted), then default to
  // the first skill once its list loads.
  useEffect(() => {
    setSelectedSkillId('');
  }, [entityType]);
  useEffect(() => {
    if (selectedSkillId === '' && headerSkills.length > 0) setSelectedSkillId(headerSkills[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSkills]);

  const existingTopicForSkill = typeTopics.find((t) => t.skillId === selectedSkillId);

  // Poll the generation job so the button shows progress until it finishes,
  // then refresh the topics above — same pattern as DocumentDetail's
  // dashboard-cards "Regenerate" button.
  const genJobQuery = useQuery({
    queryKey: ['job', genJobId],
    queryFn: () => resources.getJob(genJobId!),
    enabled: genJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const genJobStatus = genJobQuery.data?.status;
  const generating = genJobId != null && genJobStatus !== 'COMPLETED' && genJobStatus !== 'FAILED';

  useEffect(() => {
    if (genJobStatus === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['managed-knowledge-enrichments', projectId, entityType] });
    } else if (genJobStatus === 'FAILED') {
      setGenError(genJobQuery.data?.error ?? 'Failed to generate insight topics');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genJobStatus, genJobQuery.data?.error, projectId, entityType]);

  const handleGenerate = async (skillId: number, entityId?: number) => {
    setGenError(null);
    try {
      const { jobId } = await resources.generateManagedEntityTypeEnrichment(projectId, entityType, skillId, entityId);
      setGenSkillId(skillId);
      setGenJobId(jobId);
    } catch (err) {
      setGenError(extractJobErrorMessage(err));
    }
  };

  const marketFeedQuery = useQuery({
    queryKey: ['managed-knowledge-market-feed', projectId, entityType],
    queryFn: () =>
      resources.entityMarketFeeds.list({
        filter: { projectId: [projectId], entityType: [entityType] },
        sort: '-publishedAt',
        pageSize: 500,
      }),
    enabled: Number.isFinite(projectId) && !!entityType,
  });
  const marketFeedItems: EntityMarketFeed[] = marketFeedQuery.data?.data ?? [];

  // Same job-polling pattern as the insight-topics generation above.
  const marketFeedJobQuery = useQuery({
    queryKey: ['job', marketFeedJobId],
    queryFn: () => resources.getJob(marketFeedJobId!),
    enabled: marketFeedJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const marketFeedJobStatus = marketFeedJobQuery.data?.status;
  const marketFeedGenerating = marketFeedJobId != null && marketFeedJobStatus !== 'COMPLETED' && marketFeedJobStatus !== 'FAILED';

  useEffect(() => {
    if (marketFeedJobStatus === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['managed-knowledge-market-feed', projectId, entityType] });
    } else if (marketFeedJobStatus === 'FAILED') {
      setMarketFeedError(marketFeedJobQuery.data?.error ?? 'Failed to generate market feed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketFeedJobStatus, marketFeedJobQuery.data?.error, projectId, entityType]);

  // Only one project-wide job can run at a time (see assertProjectAvailable
  // on the backend) — both generation buttons share this flag so a click
  // while the other's job is in flight can't 409.
  const anyProjectJobGenerating = generating || marketFeedGenerating;

  const handleGenerateMarketFeed = async () => {
    setMarketFeedError(null);
    try {
      const { jobId } = await resources.generateEntityMarketFeed(projectId, entityType);
      setMarketFeedJobId(jobId);
    } catch (err) {
      setMarketFeedError(extractJobErrorMessage(err));
    }
  };

  const { entities, isLoading: entitiesLoading, setManaged } = useEntityTypeEntities(projectId, entityType);

  const color = hashSemanticColor(entityType);
  const typeLabel = formatEntityTypeLabel(entityType);

  // An ENTITY-scope topic is titled by its skill alone, the same for every
  // entity, so its tab also names the entity.
  const tabLabel = (key: string) => {
    if (key === MARKET_FEED_TAB) return 'Market Feed';
    const topic = topicsByTabKey.get(key);
    if (!topic) return '';
    const entityName = topic.entityId != null ? entities.find((e) => e.id === topic.entityId)?.name : undefined;
    return entityName ? `${topic.topicTitle} — ${entityName}` : topic.topicTitle;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5, pb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton size="small" onClick={() => navigate(`/projects/${projectId}`)}>
          <KeyboardBackspaceIcon fontSize="small" />
        </IconButton>
        <IconTile icon={<BookmarksOutlinedIcon fontSize="inherit" />} color={color} size="md" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-sans)',
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: 'var(--color-ink)',
            }}
          >
            {typeLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Managed project knowledge
          </Typography>
        </Box>
        <Pill color={color}>
          {entities.length} entit{entities.length === 1 ? 'y' : 'ies'}
        </Pill>
        {canGenerate && (
          <Stack direction="row" alignItems="center" spacing={1}>
            {anyProjectJobGenerating && <CircularProgress size={16} />}
            {genError && (
              <Typography variant="caption" color="error">
                {genError}
              </Typography>
            )}
            {marketFeedError && (
              <Typography variant="caption" color="error">
                {marketFeedError}
              </Typography>
            )}
            {headerSkills.length > 0 && (
              <Select
                size="small"
                value={selectedSkillId}
                onChange={(e) => setSelectedSkillId(Number(e.target.value))}
                disabled={anyProjectJobGenerating}
                sx={{ minWidth: 160 }}
              >
                {headerSkills.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            )}
            <Tooltip
              title={
                headerSkills.length === 0
                  ? 'No skills defined for this entity type yet — add one in Global Settings > Entity Type Skills'
                  : 'Research these entities with AI (incl. web search) and generate the selected skill\'s insight topic'
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  onClick={() => selectedSkillId && handleGenerate(selectedSkillId)}
                  disabled={anyProjectJobGenerating || headerSkills.length === 0 || !selectedSkillId}
                >
                  {existingTopicForSkill ? 'Regenerate Insights' : 'Generate Insights'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Research the past week's news, articles, and social activity for these entities via AI web search">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NewspaperOutlinedIcon />}
                  onClick={handleGenerateMarketFeed}
                  disabled={anyProjectJobGenerating}
                >
                  {marketFeedItems.length > 0 ? 'Refresh Market News' : 'Find Market News'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
        <Tooltip title="Open the market feed in a tab">
          <Button
            size="small"
            variant="outlined"
            startIcon={<DynamicFeedOutlinedIcon />}
            onClick={() => openTab(MARKET_FEED_TAB)}
          >
            Market Feed
          </Button>
        </Tooltip>
        {activeTab === MAIN_TAB && <CardLayoutToggle value={layout} onChange={setLayout} />}
      </Stack>

      <Tabs
        value={activeTab === MAIN_TAB || openTabs.includes(activeTab) ? activeTab : false}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        <Tab label="Main" value={MAIN_TAB} sx={{ minHeight: 40 }} />
        {openTabs.map((key) => {
          const label = tabLabel(key);
          return (
            <Tab
              key={key}
              value={key}
              label={<ClosableTabLabel label={label} onClose={() => closeTab(key)} />}
              onKeyDown={(e) => {
                if (e.key === 'Delete' || e.key === 'Backspace') closeTab(key);
              }}
              sx={{ minHeight: 40 }}
            />
          );
        })}
      </Tabs>

      {activeTab === MARKET_FEED_TAB ? (
        <MarketFeedTab items={marketFeedItems} typeLabel={typeLabel} />
      ) : activeTab !== MAIN_TAB ? (
        (() => {
          const topic = topicsByTabKey.get(activeTab);
          return topic ? <InsightTopicTab key={topic.id} topic={topic} /> : null;
        })()
      ) : (
        <MainTab
          skills={skills}
          topics={topics}
          entities={entities}
          entitiesLoading={entitiesLoading}
          typeLabel={typeLabel}
          layout={layout}
          canEdit={canEdit}
          canRunSkills={canGenerate}
          runningSkillId={generating ? genSkillId : null}
          runDisabled={anyProjectJobGenerating}
          onRunSkill={handleGenerate}
          onViewTopic={openTopicTab}
          onManagedChange={setManaged}
          selectedEntityId={selectedEntityId}
          onSelectEntity={setSelectedEntityId}
        />
      )}
    </Box>
  );
}

/** A closable tab's label — the title (ellipsized when long) and a close "×" that doesn't also select the tab. */
function ClosableTabLabel({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <Stack component="span" direction="row" alignItems="center" spacing={0.75} sx={{ maxWidth: '100%' }}>
      <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      <CloseIcon
        role="button"
        aria-label={`Close ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        sx={{
          fontSize: 16,
          flexShrink: 0,
          borderRadius: '50%',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        }}
      />
    </Stack>
  );
}
